import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControlLabel, Checkbox, Snackbar, Alert } from '@mui/material';
import { CategoriaTorneio } from '../../../../services/categoriaTorneioService';
import { gerarPartidasCategoria } from '../../../../services/partidaService';
import { StatusCategoriaTorneio } from '../../../../types/categoriaTorneio';
import { temFaseDeGrupos } from '../../../../utils/temFaseDeGrupos';

interface GerarPartidasButtonProps {
  categoriaId: string;
  categoria: CategoriaTorneio;
  inscricoesPagas: number;
  onRefresh?: () => void;
  buttonClassName?: string;
  buttonLabel?: string;
}

/**
 * Botão "Gerar Partidas" com diálogo de confirmação.
 * Comportamento único compartilhado entre a aba "Sobre" (CategoryAboutTab) e a
 * aba "Partidas" (CategoryMatchesTab): mesma validação de status, mesma mensagem
 * de distribuição de grupos e a mesma mutation gerarPartidasCategoria.
 */
const GerarPartidasButton: React.FC<GerarPartidasButtonProps> = ({
  categoriaId,
  categoria,
  inscricoesPagas,
  onRefresh,
  buttonClassName = 'btn btn-outline-success w-100',
  buttonLabel = 'Gerar Partidas',
}) => {
  const [showGenerateMatchesDialog, setShowGenerateMatchesDialog] = useState(false);
  const [generatingMatches, setGeneratingMatches] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const fasesCategoria = categoria.fasesCategoria || [];
  const todasPartidas = fasesCategoria.flatMap((f) => f.partidas || []);
  const partidasAtivas = todasPartidas.filter((p) => p.status !== 'CANCELADA');
  const totalPartidas = partidasAtivas.length;

  const handleGenerateMatches = async () => {
    setGeneratingMatches(true);
    setShowGenerateMatchesDialog(false);
    setConfirmDelete(false);
    try {
      await gerarPartidasCategoria(categoriaId);
      onRefresh?.();
    } catch (error) {
      console.error('Erro ao gerar partidas:', error);
      // Surface the backend message (ex.: inscritos não múltiplos do tamanho do grupo
      // no rodízio) em vez de falhar em silêncio — o caminho de erro é legítimo/frequente.
      const gqlMsg = (error as { graphQLErrors?: Array<{ message?: string }> })?.graphQLErrors?.[0]?.message;
      const msg = gqlMsg || (error as { message?: string })?.message
        || 'Não foi possível gerar as partidas. Tente novamente.';
      setErrorMsg(msg);
    } finally {
      setGeneratingMatches(false);
    }
  };

  const getValidationMessage = () => {
    const validStatus = categoria.status === StatusCategoriaTorneio.INSCRICAO_ENCERRADA ||
                       categoria.status === StatusCategoriaTorneio.EM_ANDAMENTO;

    if (!validStatus) {
      return 'As partidas só podem ser geradas quando o status da categoria for "Inscrição Encerrada" ou "Em Andamento".';
    }

    if (inscricoesPagas < 2) {
      return 'É necessário no mínimo 2 inscrições confirmadas para gerar as partidas.';
    }

    return null;
  };

  const canGenerateMatches = getValidationMessage() === null;
  const hasExistingMatches = totalPartidas > 0;

  const getGroupDistributionMessage = () => {
    if (!temFaseDeGrupos(categoria.modeloDisputa) || categoria.modeloDisputa === 'GRUPO_UNICO') return null;

    const numGrupos = categoria.quantidadeGrupos || 0;
    const totalEquipes = inscricoesPagas;
    const classificados = categoria.classificadosPorGrupo || 0;

    if (numGrupos === 0 || totalEquipes === 0) return null;

    const equipesBase = Math.floor(totalEquipes / numGrupos);
    const gruposComMais = totalEquipes % numGrupos;
    const gruposComMenos = numGrupos - gruposComMais;

    const allWillQualify = equipesBase < classificados + 1;

    let message = '';
    if (gruposComMais === 0) {
      message = `Serão criados ${numGrupos} grupos com ${equipesBase} equipes cada.`;
    } else {
      message = `Serão criados ${gruposComMais} grupos com ${equipesBase + 1} equipes e ${gruposComMenos} grupos com ${equipesBase} equipes.`;
    }

    if (allWillQualify) {
      message += '\n\nATENÇÃO: Todos os participantes de um ou mais grupos serão classificados para a próxima fase. '+
                  'Considere ajustar o número de classificados por grupo ou o número de grupos.';
    }

    return message;
  };

  return (
    <>
      <button
        className={buttonClassName}
        onClick={() => setShowGenerateMatchesDialog(true)}
        data-testid="gerar-partidas-btn"
      >
        {buttonLabel}
      </button>

      {/* Dialog de Confirmação para Gerar Partidas */}
      <Dialog open={showGenerateMatchesDialog} onClose={() => { setShowGenerateMatchesDialog(false); setConfirmDelete(false); }}>
        <DialogTitle>Gerar Partidas</DialogTitle>
        <DialogContent>
          {canGenerateMatches ? (
            hasExistingMatches ? (
              <>
                <p>Esta categoria já possui partidas criadas. Para gerar novamente é preciso deletar todas as que já estão criadas. Você confirma deletar todas as partidas atuais e criar novas?</p>
                {getGroupDistributionMessage() && (
                  <p className="text-muted small" style={{ whiteSpace: 'pre-line' }}>{getGroupDistributionMessage()}</p>
                )}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={confirmDelete}
                      onChange={(e) => setConfirmDelete(e.target.checked)}
                    />
                  }
                  label="Confirmo deletar todas as partidas atuais e criar novas"
                />
              </>
            ) : (
              <>
                <p>Tem certeza que deseja gerar as partidas para esta categoria?</p>
                {getGroupDistributionMessage() && (
                  <p className="text-muted small" style={{ whiteSpace: 'pre-line' }}>{getGroupDistributionMessage()}</p>
                )}
                <p className="text-muted small">Esta ação criará todas as partidas baseadas no modelo de disputa configurado.</p>
              </>
            )
          ) : (
            <div className="alert alert-warning" role="alert">
              <strong>Não é possível gerar partidas:</strong>
              <p className="mb-0 mt-2">{getValidationMessage()}</p>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowGenerateMatchesDialog(false); setConfirmDelete(false); }} disabled={generatingMatches}>Cancelar</Button>
          <Button
            onClick={handleGenerateMatches}
            variant="contained"
            color="success"
            disabled={!canGenerateMatches || generatingMatches || (hasExistingMatches && !confirmDelete)}
            data-testid="gerar-partidas-confirm"
          >
            {generatingMatches ? 'Gerando...' : 'Gerar Partidas'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!errorMsg}
        autoHideDuration={8000}
        onClose={() => setErrorMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          onClose={() => setErrorMsg(null)}
          sx={{ width: '100%' }}
          data-testid="gerar-partidas-erro"
        >
          {errorMsg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default GerarPartidasButton;
